// MARK://dhk8/admin-shell/stores/auth-store
// Session for the merchant admin. Login is PIN (4-digit id + 6-digit password) or
// email/password against dhk8-platform-auth. On success we persist
// { tenantId, userId, roles, token, fullName } to localStorage so refresh keeps the
// session; service calls (lib/api.ts) send `x-dhk8-tenant-id` / `x-dhk8-user-id` /
// `Authorization: Bearer <token>`.
//
// Routing guards (App.tsx) read `roles` — the "superadmin" role unlocks the
// "Cuentas de terceros" module and the impersonate action.
//
// Dev: VITE_DEV_LOGIN=1 → `devLogin()` seeds a fake owner+superadmin session so the
// preview build (no backend) can click straight into the admin.

import { create } from 'zustand'
import { svcUrl, apiFetch, apiData } from '../lib/api.js'
import { PREVIEW_STUB } from '../lib/env.js'

export interface AdminSession {
  /** Active tenantId (= live or test depending on `activeEnv`, or an impersonated tenant). */
  tenantId: string
  userId: string
  fullName: string
  email: string
  roles: string[]
  token: string | null
  /** Refresh token de larga vida; rota el access token (15 min) de forma
   *  transparente en lib/api.ts. Sin esto, todo 401 rompía la sesión admin. */
  refreshToken?: string | null
  // ── Stripe-style test/live (Opción A): one Account, two persistent tenants ──
  accountId?: string | null
  liveTenantId?: string | null
  testTenantId?: string | null
  /** Slug humano de la cuenta (ej. "savis-coffee-gdl") — base de los enlaces
   *  públicos compartibles (queue.nintaigo.com/queue/<slug>, etc.). */
  slug?: string | null
  activeEnv?: 'test' | 'live'
  /** Set while impersonating another principal; null normally.
   *  - kind=tenant → superadmin viewing another tenant's data.
   *  - kind=user   → owner of the *same* tenant viewing the app as another staff member
   *                  (manager/cashier/waiter) — used for support / overrides without
   *                  asking for their PIN.
   *  In both cases the previous (real) identity is preserved under `previous`
   *  so `stopImpersonating()` can restore it cleanly. */
  impersonating?: {
    kind: 'tenant' | 'user'
    tenantId: string
    /** Display name shown in the AdminLayout banner. */
    name: string
    /** For kind=user: the impersonated user's id + role. */
    userId?: string
    role?: string
    /** Real identity to restore on stop. */
    previous?: { tenantId: string; userId: string; fullName: string; roles: string[]; token: string | null }
  } | null
}

const STORAGE_KEY = 'dhk8-admin-session'

function load(): AdminSession | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as AdminSession) : null
  } catch { return null }
}
function save(s: AdminSession | null): void {
  if (typeof localStorage === 'undefined') return
  if (s) localStorage.setItem(STORAGE_KEY, JSON.stringify(s))
  else localStorage.removeItem(STORAGE_KEY)
}

interface AuthState {
  session: AdminSession | null
  loading: boolean
  error: string | null
  pinLogin: (pinId: string, pinPassword: string) => Promise<void>
  emailLogin: (email: string, password: string) => Promise<void>
  devLogin: () => void
  logout: () => void
  /** Superadmin: enter/exit a tenant's data (UI-only flag; the next service call
   *  uses this tenantId). */
  impersonate: (tenantId: string, name: string) => void
  /** Owner: enter/exit another staff member's session within the same tenant
   *  ("Ver como…"). Audit logging happens in the page that triggers this; the
   *  store just swaps userId/roles/token. */
  impersonateUser: (input: { userId: string; fullName: string; role: string; token?: string | null }) => void
  stopImpersonating: () => void
  /** Replace/patch the session (used by /onboarding after provisioning hands back a token). */
  setSession: (s: AdminSession) => void
  hasRole: (role: string) => boolean
  /** Resolve the Account (`GET /v1/accounts/me`) and wire test/live tenant ids.
   *  Defaults `activeEnv` to 'test' so a fresh signup lands in the sandbox dataset. */
  loadAccount: () => Promise<void>
  /** Flip between the test and live datasets. Llama POST /v1/auth/switch-env
   *  que re-emite JWT scopeado al targetTenantId; reemplaza token/userId/tenantId.
   *  Async porque hace fetch al backend. */
  switchEnv: (env: 'test' | 'live') => Promise<void>
}

interface AuthResponse { token?: string; refreshToken?: string; user: { id: string; email: string; fullName: string; tenantId: string; branchId?: string; roles?: string[]; role?: string } }

function fromAuthResponse(r: AuthResponse): AdminSession {
  return {
    tenantId: r.user.tenantId,
    userId: r.user.id,
    fullName: r.user.fullName,
    email: r.user.email,
    roles: r.user.roles ?? (r.user.role ? [r.user.role] : []),
    token: r.token ?? null,
    refreshToken: r.refreshToken ?? null,
    impersonating: null,
  }
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: load(),
  loading: false,
  error: null,

  async pinLogin(pinId, pinPassword) {
    set({ loading: true, error: null })
    try {
      const r = await apiFetch<AuthResponse>(svcUrl('dhk8-platform-auth', '/v1/auth/pin-login'), {
        method: 'POST', body: { pinId, pinPassword },
      })
      const s = fromAuthResponse(r); save(s); set({ session: s, loading: false })
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'PIN incorrecto' }); throw err
    }
  },

  async emailLogin(email, password) {
    set({ loading: true, error: null })
    try {
      const r = await apiFetch<AuthResponse>(svcUrl('dhk8-platform-auth', '/v1/auth/login'), {
        method: 'POST', body: { email, password },
      })
      const s = fromAuthResponse(r); save(s); set({ session: s, loading: false })
    } catch (err) {
      set({ loading: false, error: err instanceof Error ? err.message : 'Credenciales inválidas' }); throw err
    }
  },

  devLogin() {
    const s: AdminSession = {
      tenantId: 'tnt_demo', userId: 'usr_owner_demo', fullName: 'Dueño Demo',
      email: 'dueno@nintaigo.demo', roles: ['owner', 'superadmin'], token: null,
      accountId: 'acct_demo', testTenantId: 'tnt_demo', liveTenantId: 'tnt_demo_live', activeEnv: 'test',
      impersonating: null,
    }
    save(s); set({ session: s, error: null })
  },

  logout() { save(null); set({ session: null }) },

  impersonate(tenantId, name) {
    const s = get().session
    if (!s) return
    const previous = { tenantId: s.tenantId, userId: s.userId, fullName: s.fullName, roles: s.roles, token: s.token }
    const next: AdminSession = { ...s, tenantId, impersonating: { kind: 'tenant', tenantId, name, previous } }
    save(next); set({ session: next })
  },
  impersonateUser({ userId, fullName, role, token }) {
    const s = get().session
    if (!s) return
    const previous = { tenantId: s.tenantId, userId: s.userId, fullName: s.fullName, roles: s.roles, token: s.token }
    const next: AdminSession = {
      ...s,
      userId,
      fullName,
      roles: [role],
      token: token ?? s.token,
      impersonating: { kind: 'user', tenantId: s.tenantId, name: fullName, userId, role, previous },
    }
    save(next); set({ session: next })
  },
  stopImpersonating() {
    const s = get().session
    if (!s) return
    const prev = s.impersonating?.previous
    const restored: AdminSession = prev
      ? { ...s, tenantId: prev.tenantId, userId: prev.userId, fullName: prev.fullName, roles: prev.roles, token: prev.token, impersonating: null }
      // Legacy fallback (sessions persisted before this change had no `previous`):
      // we can only safely clear the flag and reset to the test tenant.
      : { ...s, tenantId: s.testTenantId ?? s.tenantId, impersonating: null }
    save(restored); set({ session: restored })
  },

  setSession(s) { save(s); set({ session: s, error: null }) },

  hasRole(role) {
    const s = get().session
    return !!s && s.roles.includes(role)
  },

  async loadAccount() {
    const s = get().session
    if (!s) return
    if (s.impersonating) return // superadmin viewing someone else — leave the active tenant alone
    const { getAccount } = await import('../lib/clients/account.js')
    try {
      const acct = await getAccount(s.tenantId)
      // IMPORTANTE (fix 2026-06-01): re-leer la sesión ACTUAL del store, NO
      // spreadear el `s` capturado al inicio. `getAccount` pasa por apiData, que
      // ante un 401 (access token vencido) dispara ensureRefreshed→applyRefreshedToken
      // y PERSISTE token/refreshToken nuevos DURANTE este await. Si construyéramos
      // `next` desde el `s` viejo, pisaríamos ese token recién rotado con el expirado
      // (y el refreshToken con el ya-revocado) → el siguiente request 401ea y el
      // refresh subsecuente manda un refresh revocado (replay) → sesión muerta.
      // Era invisible mientras erp-products REST no verificaba el JWT; el grafo lo
      // destapó. Mergeamos los campos de cuenta sobre la sesión vigente.
      const cur = get().session ?? s
      // 2026-05-24: default 'live'. Antes era 'test' lo cual mandaba al admin
      // a buscar testTenantId que para clientes que NO tienen entorno test
      // (la mayoria de operacion real, ej. Abraham SAVIs) caia a un tenantId
      // invalido o borrado → tenant-guard rechazaba con TENANT_MISMATCH 403.
      const env: 'test' | 'live' = cur.activeEnv ?? 'live'
      // Si pidieron 'test' pero el tenant NO tiene test asociado, NO cambies
      // el tenantId — mantén el live para no bloquear las llamadas.
      const wantTest = env === 'test' && typeof acct.testTenantId === 'string' && acct.testTenantId.length > 0
      const activeTenantId = wantTest ? (acct.testTenantId as string) : acct.liveTenantId
      const next: AdminSession = { ...cur, accountId: acct.accountId, liveTenantId: acct.liveTenantId, testTenantId: acct.testTenantId, slug: acct.slug ?? cur.slug ?? null, activeEnv: wantTest ? 'test' : 'live', tenantId: activeTenantId }
      save(next); set({ session: next })
    } catch { /* keep the session as-is */ }
  },

  async switchEnv(env) {
    const s = get().session
    if (!s) return
    if (env === s.activeEnv) return
    // 2026-05-24: si el tenant no tiene testTenantId, no se puede switchear a
    // test — quedate en live. Evita FORBIDDEN al downstream. Para SAVIs el
    // testTenantId ya está provisionado (paso 6); este guard sigue siendo
    // defensivo para tenants futuros que no tengan TEST aún.
    if (env === 'test' && (typeof s.testTenantId !== 'string' || s.testTenantId.length === 0)) {
      // eslint-disable-next-line no-console
      console.warn('[auth-store] switchEnv(test) ignored — tenant has no testTenantId')
      return
    }
    // PREVIEW_STUB: comportamiento client-only.
    if (PREVIEW_STUB) {
      const activeTenantId = env === 'live' ? (s.liveTenantId ?? s.tenantId) : (s.testTenantId ?? s.tenantId)
      const next: AdminSession = { ...s, activeEnv: env, tenantId: activeTenantId, impersonating: null }
      save(next); set({ session: next })
      return
    }
    // Llama backend POST /v1/auth/switch-env que re-emite el JWT scopeado al
    // tenant del env destino (Stripe-style Account vincula live+test). Sin
    // esto, cambiar solo el tenantId enviado en queries dispara backend
    // tenantGuard 403 porque el JWT actual es del tenant source.
    // Usa apiData (no apiFetch) porque el backend devuelve envelope
    // { ok: true, data: { token, refreshToken, user, env, tenantId } }; apiData
    // lo desempaqueta. Con apiFetch resp.token === undefined → token: null
    // en session → próxima request 401 (regression del JWT existente).
    type SwitchEnvResp = {
      token: string
      refreshToken?: string
      user: { id: string; email: string; fullName: string; tenantId: string; role: string; roles?: string[] }
    }
    const resp = await apiData<SwitchEnvResp>(
      svcUrl('dhk8-platform-auth', '/v1/auth/switch-env'),
      { method: 'POST', body: { targetEnv: env } },
    )
    // Guard: si la respuesta no tiene token, NO sobreescribir la session;
    // mantener el JWT actual es mejor que dejar al admin sin auth.
    if (typeof resp.token !== 'string' || resp.token.length === 0) {
      throw new Error('switch-env: respuesta sin token')
    }
    const nextSession: AdminSession = {
      ...s,
      userId: resp.user.id,
      tenantId: resp.user.tenantId,
      email: resp.user.email,
      fullName: resp.user.fullName,
      roles: resp.user.roles ?? [resp.user.role],
      token: resp.token,
      refreshToken: resp.refreshToken ?? s.refreshToken ?? null,
      activeEnv: env,
      impersonating: null,
    }
    save(nextSession); set({ session: nextSession, error: null })
  },
}))

/** Non-hook accessor used by lib/api.ts to attach headers. */
export function authSession(): { tenantId: string; userId: string; token: string | null } | null {
  const s = useAuthStore.getState().session
  if (!s) {
    // In preview, allow calls before login (clients short-circuit to mocks anyway).
    return PREVIEW_STUB ? { tenantId: 'tnt_demo', userId: 'usr_owner_demo', token: null } : null
  }
  return { tenantId: s.tenantId, userId: s.userId, token: s.token }
}

/** The active tenant id (live | test | impersonated). Empty string if no session.
 *  Used by clients whose backend takes `tenantId` as a query param (not just the header). */
export function authTenantId(): string {
  return useAuthStore.getState().session?.tenantId ?? (PREVIEW_STUB ? 'tnt_demo' : '')
}

/** Refresh token actual (o null). Lo usa lib/api.ts para rotar el access token
 *  cuando un request devuelve 401. */
export function getStoredRefreshToken(): string | null {
  return useAuthStore.getState().session?.refreshToken ?? null
}

/** Reemplaza el access token (y opcionalmente el refresh) en la sesión activa y
 *  lo persiste, sin tocar el resto del estado. Lo invoca lib/api.ts tras una
 *  rotación exitosa para que el reintento + requests siguientes usen el nuevo JWT. */
export function applyRefreshedToken(token: string, refreshToken?: string | null): void {
  const s = useAuthStore.getState().session
  if (!s) return
  const next: AdminSession = {
    ...s,
    token,
    ...(refreshToken ? { refreshToken } : {}),
  }
  save(next)
  useAuthStore.setState({ session: next })
}
